import { useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api, imHintergrund } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import type { Auswahleintrag } from './Auswahlfeld'
import { gilt, laufenderMonat } from './Stilllegung'
import { bisVorStilllegung, mitBis } from './Bisvermerk'
import { Dialogfenster } from './Dialogfenster'
import type { Angemeldet, Anwenderangabe, Anwenderauftrag, Rolle } from './typen'
import { beschriftung, periodenname, t, zeitpunkt } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * Die Anwenderverwaltung (8.1, R358–R362).
 *
 * **Hier stand bis zum 2026-09-11 ein Platzhalter** — eine Liste der Rollen
 * mit „Anmelden als …", die keine Rechte vergab, weil es keine gab. R351 hatte
 * versprochen: Kommt die Anmeldung, verschwindet er. Er ist verschwunden.
 *
 * **Der Rechtespiegel ist der wichtigste Teil dieses Bildschirms.** Weil
 * Fehlendes nicht angezeigt, sondern weggelassen wird (R360), sieht ein
 * Anwender ohne Recht keine Meldung, sondern eine leere Liste — und ruft an.
 * Die Antwort darauf muss in zehn Sekunden zu finden sein: Verben,
 * Geltungsbereich und letzte Anmeldung stehen in einer Zeile.
 */

/** Die Reihenfolge der Verben ist die aus 8.1 — nicht alphabetisch. */
const VERBORDNUNG = [
  'lesen',
  'pflegen',
  'importieren',
  'rechnen',
  'freigeben',
  'freigabe_zuruecknehmen',
  'periode_wiedereroeffnen',
  'verwalten',
  'pruefregeln_festlegen',
]

/** Ersatz, wenn der Wahlvorrat der Rollen im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_ROLLEN: Rolle[] = []

/** Was im Geltungsbereich zur Wahl steht (R1458). */
type Geltungsvorrat = { einheiten: Auswahleintrag[]; ledger: Auswahleintrag[] }
const KEIN_VORRAT: Geltungsvorrat = { einheiten: [], ledger: [] }

/** Wie ein gewählter Eintrag heißt — der Name, und nur ohne Treffer die Kennung. */
function geltungsname(vorrat: Auswahleintrag[], schluessel: string): string {
  return vorrat.find((e) => e.wert === schluessel)?.text ?? schluessel
}

export function Anwenderliste({ angemeldet }: { angemeldet: Angemeldet | null }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('anwender:anwenderliste')

  // Die Liste ist der Inhalt der Seite — ein Hauptabruf (R1211). Kam sie
  // nicht, steht „Ließ sich nicht laden" statt einer leeren Tafel (R1210).
  const listenabruf = useAbruf(() => api.anwenderliste(), [])
  const liste = listenabruf.wert
  // Rollennamen und Wahlvorrat: im Hintergrund (7.2 Abschnitt 30, R1007; R1211).
  const rollen = useHintergrundabruf(() => api.rollen(), [], KEINE_ROLLEN)
  // Gesellschaften und Bewertungsebenen mit Namen (R1458): im Hintergrund wie die Rollen.
  const geltungsvorrat = useHintergrundabruf<Geltungsvorrat>(
    () =>
      Promise.all([api.stammdatenliste('einheit'), api.stammdatenliste('ledger')]).then(([e, l]) => ({
        einheiten: e.eintraege.map(ausStammdatum),
        ledger: l.eintraege.map(ausStammdatum),
      })),
    [],
    KEIN_VORRAT,
  )
  const [blattFuer, setBlattFuer] = useState<Anwenderangabe | 'neu' | null>(null)
  const [befund, setBefund] = useState<Sprachschluessel | null>(null)
  const darf = useDarf()

  const rollenname = (schluessel: string): string => {
    const r = rollen.find((x) => x.schluessel === schluessel)
    return r ? beschriftung(r.namen) || r.code : schluessel
  }

  return (
    <section className="anwenderliste">
      {/*
        **Solange das Blatt offen steht, ist der Hintergrund nicht bedienbar**
        (R451). Der Hof des Fensters liegt darüber und nimmt der Maus den Zugriff —
        der Tabulatortaste nahm er ihn nicht. Wer im Blatt weitertabbte, landete
        auf *Anwender anlegen* und den *Einrichten*-Knöpfen dahinter, drückte und
        sah nichts geschehen: Knöpfe, die es für ihn in diesem Augenblick gar
        nicht gibt.

        `inert` sagt das einmal für den ganzen Teilbaum, statt es an jedem Knopf
        zu wiederholen — und deckt damit auch, was hier später noch dazukommt.
      */}
      <div inert={blattFuer ? '' : undefined}>
        <h1>{t('bereich.anwender.liste')}</h1>

        {darf('anwenderAnlegen') && (
          <div className="brettleiste">
            <button type="button" className="haupt" onClick={() => setBlattFuer('neu')}>
              {t('anwender.anlegen')}
            </button>
          </div>
        )}

        {befund && <p className="kachelbefund">{t(befund)}</p>}

        {listenabruf.ausgefallen ? (
          <Abrufausfall abruf={listenabruf} />
        ) : liste === null ? (
          <p className="hinweis">{t('kachel.laedt')}</p>
        ) : (
          <table ref={spaltenrahmen} className="anwendertafel ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse} />
            <thead>
              <tr>
                <th>{t('anwender.spalte.name')}</th>
                <th>{t('anwender.spalte.rolle')}</th>
                <th>{t('anwender.spalte.rechte')}</th>
                <th>{t('anwender.spalte.geltung')}</th>
                <th>{t('anwender.spalte.zuletzt')}</th>
                {darf('anwenderPflegen') && <th />}
              </tr>
            </thead>
            <tbody>
              {liste.map((a) => (
                <tr key={a.schluessel} className={a.istAktiv ? '' : 'gesperrt'}>
                  <td>
                    <strong>{a.anzeigename}</strong>
                    <span className="anmeldename">{a.anmeldename}</span>
                    {!a.istAktiv && <span className="sperrvermerk">{t('anwender.gesperrt')}</span>}
                    {a.mussKennwortWechseln && (
                      <span className="sperrvermerk">{t('anwender.musswechseln')}</span>
                    )}
                  </td>
                  <td>{rollenname(a.rolle)}</td>
                  <td className="rechtespiegel">
                    {/*
                      **Der Rechtespiegel zeigt auch, was fehlt.** Eine Liste nur
                      der erteilten Verben beantwortet die Frage „warum sieht der
                      nichts?" gerade nicht — man müsste wissen, was es alles
                      gäbe. Hier steht beides, unterschieden durch Form und
                      Schrift, nicht durch Farbe allein (R143).
                    */}
                    {VERBORDNUNG.map((v) => (
                      <span
                        key={v}
                        className={`verb ${a.rechte.includes(v) ? 'hat' : 'fehlt'}`}
                        title={t(`verb.${v}` as Sprachschluessel)}
                      >
                        {t(`verb.${v}.kurz` as Sprachschluessel)}
                      </span>
                    ))}
                  </td>
                  <td className="geltung">
                    {/* Leer heißt „auf allem" — und das muss dastehen, sonst
                        liest es jemand als „auf nichts" (R359). */}
                    {!a.einheiten?.length && !a.ledger?.length && !a.vonPeriode && !a.bisPeriode ? (
                      <span className="aufallem">{t('anwender.aufallem')}</span>
                    ) : (
                      <>
                        {/* Mit Namen, nicht mit Kennung (R1458). */}
                        {a.einheiten?.length ? (
                          <span>{a.einheiten.map((s) => geltungsname(geltungsvorrat.einheiten, s)).join(', ')}</span>
                        ) : null}
                        {a.ledger?.length ? (
                          <span>{a.ledger.map((s) => geltungsname(geltungsvorrat.ledger, s)).join(', ')}</span>
                        ) : null}
                        {/* **Gelesen wird „Januar 2026 – Dezember 2026"**
                            (R839 — Eine Periode liest sich als Monat und
                            Jahr). Bis zum 2026-09-30 stand hier die
                            gespeicherte Zahl: „202601 – 202612". */}
                        {a.vonPeriode || a.bisPeriode ? (
                          <span>
                            {a.vonPeriode ? periodenname(a.vonPeriode) : '…'} –{' '}
                            {a.bisPeriode ? periodenname(a.bisPeriode) : '…'}
                          </span>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="wann">
                    {a.zuletztAngemeldet
                      ? zeitpunkt(a.zuletztAngemeldet)
                      : t('anwender.nie')}
                  </td>
                  {/* Ohne Recht zum Pflegen fehlt die ganze Spalte, nicht nur der Knopf (R1180). */}
                  {darf('anwenderPflegen') && (
                    <td>
                      <button type="button" className="schmal" onClick={() => setBlattFuer(a)}>
                        {t('kachel.einrichten')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {blattFuer && (
        <Anwenderblatt
          anwender={blattFuer === 'neu' ? null : blattFuer}
          rollen={rollen}
          geltungsvorrat={geltungsvorrat}
          selbst={angemeldet?.anwender ?? ''}
          aufSchliessen={() => setBlattFuer(null)}
          aufBefund={setBefund}
          aufFertig={() => {
            setBlattFuer(null)
            setBefund(null)
            listenabruf.erneut()
          }}
        />
      )}
    </section>
  )
}

/**
 * Gesellschaften oder Bewertungsebenen, **zum Anhaken mit Namen** (7.4
 * Abschnitt 34.10, R1458).
 *
 * **Nichts angehakt heißt „auf allem“** (R359) — und das steht da. Was
 * stillgelegt ist, wird nicht angeboten; ist es schon gewählt, bleibt es mit
 * Vermerk stehen (R1447). Eine gewählte Kennung, die es nicht mehr gibt,
 * steht als Kennung da: So lässt sie sich wenigstens abhaken.
 */
function Geltungswahl({
  titel,
  vorrat,
  gewaehlt,
  aufAenderung,
}: {
  titel: string
  vorrat: Auswahleintrag[]
  gewaehlt: string[] | null
  aufAenderung: (gewaehlt: string[] | null) => void
}) {
  const monat = laufenderMonat()
  const ist = gewaehlt ?? []
  const zeilen = [
    ...vorrat
      .filter((e) => gilt(e.stillgelegtAb, monat) || ist.includes(e.wert))
      .map((e) => ({
        wert: e.wert,
        // Dasselbe Wort wie an jedem alten Element (7.3 Abschnitt 33, R1724).
        text: gilt(e.stillgelegtAb, monat) ? e.text : mitBis(e.text, bisVorStilllegung(e.stillgelegtAb!)),
      })),
    ...ist.filter((w) => !vorrat.some((e) => e.wert === w)).map((w) => ({ wert: w, text: w })),
  ]

  const schalte = (wert: string, an: boolean) => {
    const neu = an ? [...ist, wert] : ist.filter((w) => w !== wert)
    aufAenderung(neu.length > 0 ? neu : null)
  }

  return (
    // Gebaut wie die Rechte darüber: Häkchen untereinander, der Titel darüber.
    <div className="geltungswahl" role="group" aria-label={titel}>
      <span className="geltungstitel">{titel}</span>
      {ist.length === 0 && <small className="aufallem">{t('anwender.aufallem')}</small>}
      <div className="geltungsliste">
        {zeilen.map((z) => (
          <label key={z.wert} className="wahl">
            <input type="checkbox" checked={ist.includes(z.wert)} onChange={(e) => schalte(z.wert, e.target.checked)} />
            {z.text}
          </label>
        ))}
      </div>
    </div>
  )
}

/**
 * Das Blatt eines Anwenders — **dasselbe Muster wie in der Stammdatenpflege**
 * (R317).
 */
function Anwenderblatt({
  anwender,
  rollen,
  geltungsvorrat,
  selbst,
  aufSchliessen,
  aufFertig,
  aufBefund,
}: {
  anwender: Anwenderangabe | null
  rollen: Rolle[]
  geltungsvorrat: Geltungsvorrat
  selbst: string
  aufSchliessen: () => void
  aufFertig: () => void
  aufBefund: (befund: Sprachschluessel | null) => void
}) {
  const [entwurf, setEntwurf] = useState<Anwenderauftrag>(() =>
    anwender
      ? {
          anmeldename: anwender.anmeldename,
          anzeigename: anwender.anzeigename,
          rolle: anwender.rolle,
          rechte: anwender.rechte,
          einheiten: anwender.einheiten,
          ledger: anwender.ledger,
          vonPeriode: anwender.vonPeriode,
          bisPeriode: anwender.bisPeriode,
          istAktiv: anwender.istAktiv,
          erstkennwort: null,
        }
      : {
          anmeldename: '',
          anzeigename: '',
          rolle: 'ROL-KONZERNBUCHHALTER',
          rechte: ['lesen', 'pflegen', 'importieren', 'rechnen', 'freigeben'],
          einheiten: null,
          ledger: null,
          vonPeriode: null,
          bisPeriode: null,
          istAktiv: true,
          erstkennwort: '',
        },
  )

  const [fehler, setFehler] = useState<string | null>(null)
  const aendere = (teile: Partial<Anwenderauftrag>) => setEntwurf((a) => ({ ...a, ...teile }))

  async function uebernehmen() {
    setFehler(null)

    try {
      if (anwender) {
        await api.anwenderPflegen(anwender.schluessel, entwurf)
      } else {
        await api.anwenderAnlegen(entwurf)
      }

      aufFertig()
    } catch (f) {
      // Die API schickt ihren Befund als Schlüssel — er steht am Blatt. Eine
      // Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setFehler(fachlicherSatz(f))
      aufBefund(null)
    }
  }

  /*
    **Das übliche Fenster** (7.2 Abschnitt 41, R1260): verschieben am Kopf,
    groß und klein im Kopf, die Ecke zieht, Esc und Kreuz schließen. Der
    Schließen-Knopf im Kopf ist dem Kreuz gewichen; *Übernehmen* und
    *Abbrechen* stehen im Fuß, der beim Rollen stehen bleibt (R915).
  */
  return (
    <Dialogfenster
      titel={anwender ? t('anwender.blatt.aendern') : t('anwender.blatt.anlegen')}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button type="button" className="haupt" onClick={uebernehmen}>
            {t('kachel.uebernehmen')}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('kachel.abbrechen')}
          </button>
        </>
      }
    >
        <div className="kachelblatt">
          <label className="feld">
            <span>{t('anwender.spalte.name')}</span>
            <input
              type="text"
              value={entwurf.anzeigename}
              onChange={(e) => aendere({ anzeigename: e.target.value })}
            />
          </label>

          <label className="feld">
            <span>{t('anmeldung.name')}</span>
            <input
              type="text"
              value={entwurf.anmeldename}
              onChange={(e) => aendere({ anmeldename: e.target.value })}
            />
          </label>

          {/*
            **Die Rolle füllt die Verben vor — sie bindet sie nicht** (R358).
            Wer die Rolle später ändert, ändert das Kachelbrett und keine
            Berechtigung; deshalb steht der Vorschlag nur beim Wechsel an und
            lässt sich danach von Hand überstimmen.
          */}
          <label className="feld">
            <span>{t('anwender.spalte.rolle')}</span>
            <Auswahlfeld
              wert={entwurf.rolle}
              beschriftung={t('anwender.spalte.rolle')}
              eintraege={rollen.map((r) => ({
                wert: r.schluessel,
                text: beschriftung(r.namen) || r.code,
                suchtext: [r.schluessel, r.code, ...Object.values(r.namen)].join(' '),
              }))}
              aufWahl={async (rolle) => {
                // Ein Vorschlag im Hintergrund (R1007): Fehlt er, bleiben die Rechte stehen.
                const vorschlag = await imHintergrund(() => api.rechtevorschlag(rolle)).catch(() => entwurf.rechte)
                aendere({ rolle, rechte: vorschlag })
              }}
            />
            <small>{t('anwender.rollevorschlag')}</small>
          </label>

          <fieldset className="feld">
            <legend>{t('anwender.spalte.rechte')}</legend>
            {VERBORDNUNG.map((v) => (
              <label className="wahl" key={v}>
                <input
                  type="checkbox"
                  checked={entwurf.rechte.includes(v)}
                  onChange={(e) =>
                    aendere({
                      rechte: e.target.checked
                        ? [...entwurf.rechte, v]
                        : entwurf.rechte.filter((x) => x !== v),
                    })
                  }
                />
                {t(`verb.${v}` as Sprachschluessel)}
              </label>
            ))}
          </fieldset>

          <fieldset className="feld">
            <legend>{t('anwender.spalte.geltung')}</legend>
            <small>{t('anwender.geltung.leer')}</small>

            {/* **Ausgesucht, nicht getippt** (R1458, R327). Bis zum
                2026-10-09 stand hier je ein Textfeld für Kennungen, durch
                Komma getrennt: „EH-000008-48“. */}
            <Geltungswahl
              titel={t('anwender.geltung.einheiten')}
              vorrat={geltungsvorrat.einheiten}
              gewaehlt={entwurf.einheiten}
              aufAenderung={(einheiten) => aendere({ einheiten })}
            />
            <Geltungswahl
              titel={t('anwender.geltung.ledger')}
              vorrat={geltungsvorrat.ledger}
              gewaehlt={entwurf.ledger}
              aufAenderung={(ledger) => aendere({ ledger })}
            />

            {/*
              **Dasselbe Periodenfeld wie überall** (R839 — Eine Periode liest
              sich als Monat und Jahr; R502 — Am Bildschirm steht MM.JJJJ). Bis
              zum 2026-09-30 standen hier zwei Zahlenfelder, die `202601`
              verlangten — und einen Punkt gar nicht annahmen.

              **Leer heißt weiter „ohne Grenze"** (R359): Das Feld meldet
              `null`, solange nichts Lesbares dasteht, und genau das wird
              gespeichert.
            */}
            <Periodenfeld
              klasse="wahl periodenwahl"
              beschriftung={t('anwender.geltung.von')}
              wert={entwurf.vonPeriode}
              aufWert={(periode) => aendere({ vonPeriode: periode })}
            />

            <Periodenfeld
              klasse="wahl periodenwahl"
              beschriftung={t('anwender.geltung.bis')}
              wert={entwurf.bisPeriode}
              aufWert={(periode) => aendere({ bisPeriode: periode })}
            />
          </fieldset>

          <label className="feld">
            <span>{anwender ? t('anwender.neueskennwort') : t('anwender.erstkennwort')}</span>
            <input
              type="text"
              value={entwurf.erstkennwort ?? ''}
              placeholder={anwender ? t('anwender.kennwortbleibt') : ''}
              onChange={(e) => aendere({ erstkennwort: e.target.value || null })}
            />
            <small>{t('kennwort.richtlinie')}</small>
          </label>

          <label className="wahl">
            <input
              type="checkbox"
              checked={entwurf.istAktiv}
              disabled={anwender?.schluessel === selbst}
              onChange={(e) => aendere({ istAktiv: e.target.checked })}
            />
            {t('anwender.aktiv')}
          </label>

          {fehler && <p className="kachelbefund">{fehler}</p>}
        </div>
    </Dialogfenster>
  )
}
